import { createSelector } from 'reselect';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { AppState } from 'state/state.model';

import type { RealtimePriceState, TrackedValue } from './realtime.model';

export const getRealtimeState = (appState: AppState, tabId: string): RealtimePriceState | undefined => {
  return appState.secondaryMarket.realtime[tabId];
};

export type ComputedRealtimePrice = {
  upfrontAndSalesMargin?: number;
  settlementPrice?: TrackedValue;
};

export const getRealtimeByTradeway = createSelector(
  [getRealtimeState, (_appState: AppState, _bcsProductKey: string, tradeWay: TradeWay) => tradeWay],
  (state, tradeWay) => {
    if (!state) {
      return;
    }
    if (tradeWay === 'ClientBuy') {
      return { settlementPrice: state.askSettlementPrice, upfrontAndSalesMargin: state.askUpfrontAndSalesMargin };
    } else {
      return { settlementPrice: state.bidSettlementPrice, upfrontAndSalesMargin: state.bidUpfrontAndSalesMargin };
    }
  },
);
export const getRealtimeStatus = (appState: AppState, bcsProductKey: string): boolean => {
  return getRealtimeState(appState, bcsProductKey)?.quoteStatus ?? false;
};
export const getRealtimeBidSettlementPrice = (appState: AppState, bcsProductKey: string): TrackedValue | undefined => {
  return getRealtimeState(appState, bcsProductKey)?.bidSettlementPrice;
};

export const getRealtimeAskSettlementPrice = (appState: AppState, bcsProductKey: string): TrackedValue | undefined => {
  return getRealtimeState(appState, bcsProductKey)?.askSettlementPrice;
};

export const getRealtimeUnderlyingPrice = (appState: AppState, bcsProductKey: string): number | undefined => {
  return getRealtimeState(appState, bcsProductKey)?.referencePrice?.value;
};
export const getRealtimeUnderlyingPriceUnit = (appState: AppState, bcsProductKey: string): string | undefined => {
  return getRealtimeState(appState, bcsProductKey)?.referencePrice?.valueType;
};
export function getRealtimeLastUpdate(appState: AppState, bcsProductKey: string) {
  return getRealtimeState(appState, bcsProductKey)?.extLimitTime;
}

export const getStreamingPriceForDisplayByTradeway = createSelector(
  [
    (state: AppState, bcsProductKey: string) => getRealtimeState(state, bcsProductKey)?.extLimitTime,
    (state: AppState, bcsProductKey: string) => getRealtimeState(state, bcsProductKey)?.quoteStatus,
    (state: AppState, bcsProductKey: string) => getRealtimeState(state, bcsProductKey)?.salesMargin,
    (state: AppState, bcsProductKey: string, tradeWay: TradeWay) =>
      getRealtimeByTradeway(state, bcsProductKey, tradeWay)?.settlementPrice,
    (state: AppState, bcsProductKey: string, tradeWay: TradeWay) =>
      getRealtimeByTradeway(state, bcsProductKey, tradeWay)?.upfrontAndSalesMargin,
  ],
  (extLimitTime, quoteStatus, salesMargin, settlementPrice, upfrontAndSalesMargin) => {
    return {
      salesMargin,
      extLimitTime,
      quoteStatus,
      settlementPrice,
      upfrontAndSalesMargin,
    };
  },
);

export type StreamingPriceForDisplay = {
  extLimitTime?: string;
  quoteStatus?: boolean;
  settlementPrice?: TrackedValue;
  upfrontAndSalesMargin?: number;
  salesMargin?: number;
};
